/**
 * AdminCalRatesPanel — Host-facing Rate Management inside the Admin Calendar PWA.
 *
 * Mirrors the subset of RateManagement.js shown in the mock:
 *   • Villa selector tabs (3BHK / 4BHK / 5BHK / 12BHK)
 *   • Multi-Night Discount card (% + Edit + ON/OFF)
 *   • Villa Defaults card (Weekday, Weekend, Max Guests, Extra Guest)
 *
 * Auth: uses the calendar token (not admin JWT). Editors can save;
 * viewers get a read-only badge and the save buttons are hidden.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiX, FiEdit2, FiCheck, FiPower } from 'react-icons/fi';
import Villa12BhkPricingModePanel from './Villa12BhkPricingModePanel';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_LABELS = {
  base_price: 'Weekday Rate',
  weekend_price: 'Weekend Rate (Fri-Sun)',
  capacity: 'Max Guests',
  extra_guest_charge: 'Extra Guest /night',
};

const AdminCalRatesPanel = ({ token, role, onClose }) => {
  const isViewer = role === 'viewer';
  const [villas, setVillas] = useState([]);
  const [villaId, setVillaId] = useState(null);
  const [discountPct, setDiscountPct] = useState(15);
  const [discountEnabled, setDiscountEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [discountDraft, setDiscountDraft] = useState('');
  const [editingField, setEditingField] = useState(null); // { villa_id, field }
  const [editingFieldValue, setEditingFieldValue] = useState('');

  const flash = (text, type = 'success') => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 2600);
  };

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [v, d] = await Promise.all([
        axios.get(`${API}/api/admin-cal/${token}/rate-management/villa-overrides`),
        axios.get(`${API}/api/admin-cal/${token}/rate-management/discount-settings`),
      ]);
      setVillas(v.data || []);
      if (v.data?.length) {
        setVillaId((cur) => cur || v.data[0].villa_id);
      }
      if (typeof d.data?.night_discount_pct === 'number') setDiscountPct(d.data.night_discount_pct);
      if (typeof d.data?.enabled === 'boolean') setDiscountEnabled(d.data.enabled);
    } catch (err) {
      flash(err.response?.data?.detail || 'Failed to load rates', 'error');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Lock body scroll while panel is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const villa = villas.find(v => v.villa_id === villaId);

  const startFieldEdit = (vid, field, current) => {
    if (isViewer) return;
    setEditingField({ villa_id: vid, field });
    setEditingFieldValue(String(current));
  };
  const cancelFieldEdit = () => { setEditingField(null); setEditingFieldValue(''); };
  const saveFieldEdit = async () => {
    const amt = parseInt(editingFieldValue, 10);
    if (!Number.isFinite(amt) || amt < 0) { flash('Enter a valid number', 'error'); return; }
    setBusy(true);
    try {
      await axios.put(
        `${API}/api/admin-cal/${token}/rate-management/villa-overrides/${editingField.villa_id}`,
        { [editingField.field]: amt },
      );
      flash(`${FIELD_LABELS[editingField.field]} updated`);
      setEditingField(null);
      await refreshAll();
    } catch (err) {
      flash(err.response?.data?.detail || 'Save failed', 'error');
    }
    setBusy(false);
  };

  const openDiscountEditor = () => {
    if (isViewer) return;
    setEditingDiscount(true);
    setDiscountDraft(String(discountPct));
  };
  const cancelDiscountEditor = () => { setEditingDiscount(false); setDiscountDraft(''); };
  const saveDiscountEdit = async () => {
    const pct = parseInt(discountDraft, 10);
    if (!Number.isFinite(pct) || pct < 0 || pct > 90) {
      flash('Discount % must be an integer between 0 and 90', 'error'); return;
    }
    setBusy(true);
    try {
      const res = await axios.put(
        `${API}/api/admin-cal/${token}/rate-management/discount-settings`,
        { night_discount_pct: pct },
      );
      setDiscountPct(res.data.night_discount_pct);
      setDiscountEnabled(res.data.enabled);
      setEditingDiscount(false);
      flash(`Multi-night discount updated to ${res.data.night_discount_pct}%`);
    } catch (err) {
      flash(err.response?.data?.detail || 'Save failed', 'error');
    }
    setBusy(false);
  };

  const toggleDiscount = async () => {
    if (isViewer) return;
    const next = !discountEnabled;
    if (!next && !window.confirm('Turn OFF the multi-night discount? Guests will stop seeing the multi-night % OFF strip site-wide.')) return;
    setBusy(true);
    try {
      const res = await axios.put(
        `${API}/api/admin-cal/${token}/rate-management/discount-settings`,
        { enabled: next },
      );
      setDiscountEnabled(res.data.enabled);
      flash(next ? 'Multi-night discount turned ON' : 'Multi-night discount turned OFF');
    } catch (err) {
      flash(err.response?.data?.detail || 'Toggle failed', 'error');
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-stretch sm:items-center justify-center sm:p-4"
      data-testid="cal-rates-panel-overlay"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#1A1A2E] text-white w-full sm:max-w-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-screen sm:max-h-[92vh]"
        data-testid="cal-rates-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold tracking-tight">
                Rate <span className="text-[#F57C20]">Management</span>
              </h2>
              {isViewer && (
                <span data-testid="rates-viewer-badge" className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-blue-500/20 text-blue-300 border border-blue-400/30">
                  View only
                </span>
              )}
            </div>
            <p className="text-[10px] text-white/60 mt-0.5">
              Applies to all villas • New bookings only • Existing bookings untouched.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="cal-rates-close-btn"
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0"
            aria-label="Close rate management"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-10">
            <div className="animate-spin w-8 h-8 border-3 border-[#F57C20] border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {/* 12 BHK pricing mode toggle — editor-only interactive, viewer sees read-only */}
            {!isViewer && <Villa12BhkPricingModePanel cal token={token} />}
            {/* Villa selector */}
            <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-1 px-1 pb-1">
              {villas.map(v => {
                const active = v.villa_id === villaId;
                return (
                  <button
                    key={v.villa_id}
                    onClick={() => setVillaId(v.villa_id)}
                    data-testid={`rates-villa-tab-${v.bedrooms}bhk`}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                      active
                        ? 'bg-[#F57C20] text-white'
                        : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
                    }`}
                  >
                    {v.short_name} <span className="opacity-70 font-normal">({v.bedrooms}BHK)</span>
                    {v.has_override && (
                      <span className={`ml-1 ${active ? 'text-white' : 'text-emerald-400'}`}>★</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Multi-Night Discount card */}
            <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4" data-testid="rates-discount-card">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#F57C20] mb-1">
                Multi-Night Discount
              </p>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold">Stay Longer, Pay Less — % off on every night from the 2nd onwards</p>
                  <p className="text-[10px] text-white/60 mt-0.5">
                    Applies to all villas. Change reflects instantly on the live site & new bookings.
                  </p>
                </div>
                {!isViewer && (
                  <button
                    onClick={toggleDiscount}
                    disabled={busy}
                    data-testid="rates-discount-toggle-btn"
                    className={`flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full font-bold border flex-shrink-0 ${
                      discountEnabled
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30'
                        : 'bg-white/5 text-white/50 border-white/20'
                    }`}
                  >
                    <FiPower className="w-3 h-3" />
                    {discountEnabled ? 'ON' : 'OFF'}
                  </button>
                )}
              </div>

              <div className="mt-3 bg-black/30 rounded-lg p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] text-white/50 uppercase tracking-wider">2nd Night+ Discount</p>
                  {editingDiscount ? (
                    <div className="flex items-center gap-1 mt-1">
                      <input
                        type="number"
                        min="0"
                        max="90"
                        value={discountDraft}
                        onChange={(e) => setDiscountDraft(e.target.value)}
                        data-testid="rates-discount-input"
                        className="w-20 bg-white/10 text-white text-2xl font-bold rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#F57C20]"
                        autoFocus
                      />
                      <span className="text-2xl font-bold">%</span>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold mt-0.5">
                      {discountPct}%{!discountEnabled && <span className="ml-2 text-[10px] text-white/50 font-normal">(paused)</span>}
                    </p>
                  )}
                  {!isViewer && !editingDiscount && (
                    <button
                      onClick={openDiscountEditor}
                      data-testid="rates-discount-edit-btn"
                      className="text-[11px] text-[#F57C20] hover:underline mt-0.5 font-bold"
                    >
                      Edit
                    </button>
                  )}
                  {editingDiscount && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={saveDiscountEdit}
                        disabled={busy}
                        data-testid="rates-discount-save-btn"
                        className="text-[10px] px-2 py-1 rounded bg-[#F57C20] text-white font-bold flex items-center gap-1 disabled:opacity-50"
                      >
                        <FiCheck className="w-3 h-3" /> Save
                      </button>
                      <button
                        onClick={cancelDiscountEditor}
                        className="text-[10px] px-2 py-1 rounded bg-white/10 text-white/70 font-bold"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] text-white/60"><span className="font-bold text-white">1st night</span> at actual rate</p>
                  <p className="text-[10px] text-white/60"><span className="font-bold text-white">2nd+ night</span> at {discountPct}% off, auto-applied</p>
                </div>
              </div>
            </div>

            {/* Villa Defaults card */}
            {villa && (
              <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4" data-testid="rates-villa-defaults-card">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#F57C20] mb-0.5">
                      Villa Defaults
                    </p>
                    <p className="text-sm font-bold">{villa.short_name} — base rates & guest config</p>
                  </div>
                  {villa.has_override && (
                    <span className="text-[10px] text-emerald-400 font-bold flex items-center gap-1 flex-shrink-0">
                      ✓ Has overrides
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'base_price', label: 'Weekday Rate', prefix: 'Rs.', value: villa.base_price },
                    { key: 'weekend_price', label: 'Weekend Rate (Fri-Sun)', prefix: 'Rs.', value: villa.weekend_price },
                    { key: 'capacity', label: 'Max Guests', prefix: '', value: villa.capacity },
                    { key: 'extra_guest_charge', label: 'Extra Guest /night', prefix: 'Rs.', value: villa.extra_guest_charge },
                  ].map(f => {
                    const isEditing = editingField && editingField.villa_id === villa.villa_id && editingField.field === f.key;
                    return (
                      <div
                        key={f.key}
                        className="bg-black/30 rounded-lg p-3"
                        data-testid={`rates-field-${f.key}`}
                      >
                        <p className="text-[9px] text-white/50 uppercase tracking-wider truncate">{f.label}</p>
                        {isEditing ? (
                          <>
                            <div className="flex items-center gap-0.5 mt-1">
                              {f.prefix && <span className="text-sm font-bold">{f.prefix}</span>}
                              <input
                                type="number"
                                min="0"
                                value={editingFieldValue}
                                onChange={(e) => setEditingFieldValue(e.target.value)}
                                data-testid={`rates-field-input-${f.key}`}
                                className="w-full bg-white/10 text-white text-base font-bold rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-[#F57C20]"
                                autoFocus
                              />
                            </div>
                            <div className="flex gap-1 mt-1.5">
                              <button
                                onClick={saveFieldEdit}
                                disabled={busy}
                                data-testid={`rates-field-save-${f.key}`}
                                className="text-[10px] px-2 py-0.5 rounded bg-[#F57C20] text-white font-bold flex items-center gap-0.5 disabled:opacity-50"
                              >
                                <FiCheck className="w-3 h-3" /> Save
                              </button>
                              <button
                                onClick={cancelFieldEdit}
                                className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/70 font-bold"
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-base font-bold mt-0.5 truncate">
                              {f.prefix}{typeof f.value === 'number' ? f.value.toLocaleString('en-IN') : f.value}
                            </p>
                            {!isViewer && (
                              <button
                                onClick={() => startFieldEdit(villa.villa_id, f.key, f.value)}
                                data-testid={`rates-field-edit-${f.key}`}
                                className="text-[10px] text-[#F57C20] hover:underline font-bold mt-0.5 flex items-center gap-0.5"
                              >
                                <FiEdit2 className="w-2.5 h-2.5" /> Edit
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isViewer && (
              <p className="text-[11px] text-white/50 text-center italic py-2">
                View-only access. Ask the host for edit permissions to change rates.
              </p>
            )}
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div
            data-testid="cal-rates-toast"
            className={`absolute left-1/2 -translate-x-1/2 bottom-6 px-4 py-2 rounded-lg text-xs font-bold shadow-lg ${
              toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
            }`}
          >
            {toast.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCalRatesPanel;
